import { WaitHint } from "./WaitHint.tsx";
import { useEffect, useState } from "react";
import { Icon } from "./Workspace.tsx";
import {
  api, isExpired, type ActionCard, type ConsensusThread, type MessageDraft,
} from "../api.ts";
import { CHANNEL_LABEL, fmtDate, levelPill } from "../labels.ts";
import { goCustomer, goKnowledge } from "../navigate.ts";
import { EventTranscript } from "./EventTranscript.tsx";

/**
 * 一张动作卡 + 它的两个出口:**采纳** 和 **和 AI 讨论**。
 *
 * 「三要素缺一即不合格」是 PRD 的验收项,所以这里三样都必须画出来:
 * 动作(act)、出处(src,可展开看命中片段)、自主度(徽标 + 定档理由)。
 * 少画一样,界面上就看不出这张卡凭什么可信 —— 而那正是它区别于
 * 「侧边栏弹个建议」的地方。
 */

const QUICK = [
  { label: "🔄 让 AI 重新优化", text: "让 AI 重新优化这条建议" },
  { label: "⏰ 时机不对", text: "这个时间点不合适,客户最近在忙别的事" },
  { label: "💰 价格卡住了", text: "客户卡在价格上,比竞品贵" },
  { label: "🛠 更在意售后", text: "客户其实更在意售后,不是价格" },
];

export function ActionCardView(props: {
  card: ActionCard;
  /** 处理完(采纳/忽略)之后让父组件重新取数。 */
  onResolved: () => void;
  /** 详情页里不需要再显示客户名。 */
  hideCustomer?: boolean;
  /** 从行动导航跳过来时自动展开辩证面板(原型 goCustomerDialectic)。 */
  autoOpenConsensus?: boolean;
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const { card } = props;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showSrc, setShowSrc] = useState(false);
  const [thread, setThread] = useState<ConsensusThread | undefined>(undefined);
  const [msg, setMsg] = useState("");
  const [draft, setDraft] = useState<MessageDraft | undefined>(undefined);
  const [stance, setStance] = useState<"revised" | "held" | undefined>(undefined);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const run = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true); setErr("");
    try { await fn(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const adopt = () => run(async () => {
    await api.post(`/api/action-cards/${card.id}/adopt`);
    props.onResolved();
  });

  const ignore = () => run(async () => {
    await api.post(`/api/action-cards/${card.id}/ignore`);
    props.onResolved();
  });

  /** 从行动导航点「💬 和AI讨论」跳过来的:落地即展开,不用再点一次。 */
  useEffect(() => {
    if (props.autoOpenConsensus && !thread) void openConsensus();
    // 只在挂载时看一次 —— 人手动收起面板后不该被再弹开
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 原型里行动导航上的「💬 和AI讨论」是 `goCustomerDialectic(idx)`:
   * **跳到这家的详情再展开**,不是就地展开。有道理 —— 要改一条建议,
   * 你得同时看得见决策链和事件流,而那两样只在详情页有。
   * 在详情页里(hideCustomer)本来就在上下文中,就地展开。
   */
  const startConsensus = (): void => {
    if (props.hideCustomer) { void openConsensus(); return; }
    goCustomer(card.customerId, card.id);
  };

  const openConsensus = () => run(async () => {
    const r = await api.post<{ thread: ConsensusThread }>(`/api/action-cards/${card.id}/consensus`);
    setThread(r.thread);
    setHistoryExpanded(false);
  });

  const say = (text: string) => run(async () => {
    if (!thread || !text.trim()) return;
    const r = await api.post<ConsensusThread & { stance: "revised" | "held" }>(
      `/api/consensus/${thread.threadId}/reply`, { text: text.trim() },
    );
    setThread(r);
    setStance(r.stance);
    setMsg("");
  });

  const adoptConsensus = () => run(async () => {
    if (!thread) return;
    await api.post(`/api/consensus/${thread.threadId}/adopt`);
    props.onResolved();
  });

  const drawMessage = () => run(async () => {
    const r = await api.post<MessageDraft>(`/api/action-cards/${card.id}/message-draft`);
    setDraft(r);
  });

  return (
    <div className="card hl action-card">
      {card.consensusRounds > 0 && (
        <div className="trace">✓ 经与 AI 讨论达成共识({card.consensusRounds} 轮)· 已记录为纠正数据</div>
      )}
      <div className="row" style={{ justifyContent: "space-between" }}>
        <div className="row">
          {!props.hideCustomer && (
            <>
              {card.grade && <span className={`grade ${card.grade}`}>{card.grade}</span>}
              {/* 原型里客户名是可点的(goCustomer):点了跳到这家的详情 */}
              <button className="clickname" onClick={() => goCustomer(card.customerId)}>
                {card.customerName}
              </button>
            </>
          )}
          {card.channel && <span className="tag tN">{CHANNEL_LABEL[card.channel] ?? card.channel}</span>}
        </div>
        {/* 定档理由挂在 title 上 —— 「AI 凭什么能做到这一档」要能当场问出来 */}
        <span className={`tag ${levelPill(card.autonomyLevel)}`} title={`自主度 L${card.autonomyLevel}${card.autonomyReason ? ` · ${card.autonomyReason}` : ""}`}>
          {card.autonomyLevel >= 3 ? "可自动执行" : card.autonomyLevel === 2 ? "确认后执行" : "由你决策"}
        </span>
      </div>

      <div className={`act ${card.text.length > 140 && !expanded ? "collapsed" : ""}`}>{card.text}</div>
      {card.text.length > 140 && <button className="action-expand" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>{expanded ? "收起详细建议" : "展开完整建议"}</button>}

      <div className="src">
        <Icon name="book" size={14} />引用依据 {card.citations.length} 条
        {card.citations.length > 0 && (
          <button className="b sm" style={{ marginLeft: 8 }} onClick={() => setShowSrc(!showSrc)}>
            {showSrc ? "收起" : "看依据"}
          </button>
        )}
      </div>
      {showSrc && (
        <div className="hint">
          {card.citations.map((c, i) => (
            <div key={i} style={{ marginBottom: 6 }}>
              {c.eventSource ? <><b>{c.eventSource === "transcript" ? "沟通原文" : "沟通摘要"}</b>{c.eventOccurredAt && <span className="muted"> · {fmtDate(c.eventOccurredAt)}</span>}{c.eventVersion && c.currentEventVersion && c.eventVersion !== c.currentEventVersion && <span className="tag tW">沟通已更新，保留当时引用</span>}</> : c.documentId ? <button className="b sm" onClick={() => goKnowledge(c.partition ?? undefined, c.documentId!)}>
                📖 {c.documentTitle ?? "查看原文"}
              </button> : <b>{c.documentTitle ?? "(文档已删除,依据留存)"}</b>}
              {c.similarity && <span className="muted"> · 相关度 {Number(c.similarity).toFixed(2)}</span>}
              {c.documentUpdatedAt && <span className="muted"> · 资料更新于 {fmtDate(c.documentUpdatedAt)}</span>}
              {isExpired(c.documentExpiresAt) && <span className="tag tW" title="这份资料已过有效期，不再用于新的建议"> 已作废</span>}
              <div>{c.snippet}</div>
              {c.eventId && c.currentEventVersion != null && <EventTranscript eventId={c.eventId} />}
            </div>
          ))}
        </div>
      )}

      {err && <div className="err">{err}</div>}

      <div className="btns">
        <button className="b p" disabled={busy} onClick={adopt}><Icon name="check" size={15} />采纳建议</button>
        <button className="b" disabled={busy}
          onClick={() => (thread ? setThread(undefined) : startConsensus())}>
          <Icon name="sparkles" size={15} />和 AI 讨论
        </button>
        <button className="b" disabled={busy} onClick={drawMessage}>起草跟进消息</button>
        <button className="b q" disabled={busy} onClick={ignore}>忽略</button>
      </div>

      {draft && (
        <div className="chatbox">
          <div className="ttl">✉️ AI 起草的消息 <span>— {draft.deliveryNote}</span></div>
          {draft.warnings.map((w, i) => <div key={i} className="hint warn">⚠️ {w}</div>)}
          {draft.content.subject && <div style={{ fontWeight: 600, marginBottom: 4 }}>{draft.content.subject}</div>}
          <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{draft.content.body}</div>
          <div className="hint">意图:{draft.content.intent}</div>
          <div className="btns" style={{ marginTop: 8 }}>
            <button className="b p" onClick={() => {
              void navigator.clipboard?.writeText(
                `${draft.content.subject ? draft.content.subject + "\n\n" : ""}${draft.content.body}`,
              );
            }}>复制</button>
            <button className="b q" onClick={() => setDraft(undefined)}>关闭</button>
          </div>
        </div>
      )}

      {thread && (
        <div className="chatbox">
          <div className="ttl">
            💬 和 AI 讨论这个建议 <span>— 说出你的看法或补充现场信息,一起定下最终动作</span>
          </div>
          {thread.rounds > 0 && (
            <div className="consensus">
              <div className="ttl">
                ✓ 最新共识动作 · 第 {thread.rounds} 轮
                {stance && ` · AI ${stance === "revised" ? "承认判断偏了,改了方向" : "坚持原判并给了更强论据"}`}
              </div>
              <div>{thread.consensusText}</div>
              <div className="btns" style={{ marginTop: 8 }}>
                <button className="b p" disabled={busy} onClick={adoptConsensus}>采纳这个共识动作</button>
                <button className="b q" disabled={busy} onClick={() => setThread(undefined)}>先放一放</button>
              </div>
            </div>
          )}
          {thread.messages.length > 2 && (
            <button className="b sm q history-toggle" aria-expanded={historyExpanded}
              onClick={() => setHistoryExpanded(!historyExpanded)}>
              {historyExpanded ? "收起较早讨论" : `展开此前 ${thread.messages.length - 2} 条讨论`}
            </button>
          )}
          <div className="chat">
            {(historyExpanded ? thread.messages : thread.messages.slice(-2)).map((m, i) => (
              <div key={i} className={`msg ${m.who}`}>{m.text}</div>
            ))}
          </div>

          <div className="quick">
            {QUICK.map((q) => (
              <button key={q.label} className="b sm" disabled={busy} onClick={() => say(q.text)}>{q.label}</button>
            ))}
          </div>

          <div className="form-row row" style={{ marginTop: 8 }}>
            <input className="in" style={{ flex: 1 }} placeholder="说出你的看法或补充信息…"
              value={msg} onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") say(msg); }} />
            <button className="b p" disabled={busy || !msg.trim()} onClick={() => say(msg)}>发送</button>
          </div>
          <WaitHint active={busy} working="AI 正在思考" />
        </div>
      )}
    </div>
  );
}
